import classNames from "classnames";
import { FunctionComponent, useCallback, useEffect, useRef, useState } from "react";
import { v1 as uuid } from "uuid";
import { ReactComponent as IconX } from "@Icons/16x16/x.svg";
import "./style.scss";

interface Props {
  allowPaste?: boolean;
  autocomplete?: "off" | "on";
  autofocus?: boolean;
  disabled?: boolean;
  errorMessage?: string;
  onChange: (value: string) => void;
  onDismiss: () => void;
  onValidate?: () => void;
  pattern?: string;
  placeholder: string;
  required?: boolean;
  type: "email" | "number" | "password" | "tel" | "text";
  value?: number | string;
}

const InputSmily: FunctionComponent<Props> = ({
  allowPaste = true,
  autocomplete = "on",
  autofocus = false,
  disabled = false,
  errorMessage,
  onChange,
  onDismiss,
  onValidate,
  pattern,
  placeholder,
  required = false,
  type,
  value,
}) => {
  const [error, setError] = useState<boolean>(false);
  const input = useRef<HTMLInputElement>(null);
  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      event.preventDefault();
      onChange(event.target.value);
    },
    [onChange]
  );
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>): void => {
      if (event.key === "Enter") {
        event.preventDefault();
        if (onValidate) {
          onValidate();
        }
      }
    },
    [value]
  );
  const id = uuid();

  useEffect(() => {
    setError(!!errorMessage);
    if (errorMessage && input.current !== null) {
      input.current.focus();
    }
  }, [errorMessage]);

  return (
    <div
      className={classNames("InputSmily", {
        "InputSmily--disabled": disabled,
        "InputSmily--error": error,
      })}
    >
      <input
        autoComplete={autocomplete}
        autoFocus={autofocus}
        className="InputSmily-input"
        disabled={disabled}
        id={id}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onPaste={allowPaste ? () => true : (event) => event.preventDefault()}
        pattern={pattern}
        placeholder="&nbsp;"
        ref={input}
        required={required}
        tabIndex={0}
        type={type}
        value={value}
      />
      <label className="InputSmily-label" htmlFor={id}>
        <div className="InputSmily-label-line" />
        <div className="InputSmily-label-text">{`${placeholder}${required ? "*" : ""}`}</div>
      </label>
      {!!value && (
        <IconX
          onClick={() => {
            onDismiss();
            input.current !== null && input.current.focus();
          }}
        />
      )}
      {error && <div className="InputSmily-errorMessage">{errorMessage}</div>}
    </div>
  );
};

export default InputSmily;
